import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useForm } from 'react-hook-form'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  ShoppingCart,
  Clock,
  CheckCircle,
  XCircle,
  User,
  X
} from 'lucide-react'
// import { orderAPI, customerAPI, employeeAPI } from '../services/api' // Removed API calls
import { LoadingSpinner } from '../components'
import { OrderFormSteps } from '../components'
import { Button } from '../components'
import toast from 'react-hot-toast'

function Orders() {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [showModal, setShowModal] = useState(false)
  const [editingOrder, setEditingOrder] = useState(null)

  const queryClient = useQueryClient()

  // Fetch orders
  const { data: ordersData, isLoading } = useQuery(
    ['orders', { page: currentPage, search: searchTerm, status: selectedStatus }],
    () => orderAPI.getAll({
      page: currentPage,
      limit: 10,
      search: searchTerm,
      status: selectedStatus,
    })
  )

  // Fetch customers and employees for form
  const { data: customersData } = useQuery('customers', () => customerAPI.getAll({ limit: 100 }))
  const { data: employeesData } = useQuery('employees', () => employeeAPI.getAll({ limit: 100 }))

  // Create order mutation
  const createOrderMutation = useMutation(orderAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('orders')
      setShowModal(false)
      toast.success('Order created successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create order')
    },
  })

  // Update order mutation
  const updateOrderMutation = useMutation(
    ({ id, data }) => orderAPI.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('orders')
        setShowModal(false)
        setEditingOrder(null)
        toast.success('Order updated successfully!')
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update order')
      },
    }
  )

  // Delete order mutation
  const deleteOrderMutation = useMutation(orderAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('orders')
      toast.success('Order deleted successfully!')
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete order')
    },
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm()

  const onSubmit = (data) => {
    if (editingOrder) {
      updateOrderMutation.mutate({ id: editingOrder.id, data })
    } else {
      createOrderMutation.mutate(data)
    }
  }

  const handleEdit = (order) => {
    setEditingOrder(order)
    reset({
      customerId: order.customerId,
      orderDate: order.orderDate,
      deliveryDate: order.deliveryDate,
      status: order.status,
      totalAmount: order.totalAmount,
      advancePaid: order.advancePaid,
      balanceAmount: order.balanceAmount,
      notes: order.notes,
      assignedEmployeeId: order.assignedEmployeeId,
    })
    setShowModal(true)
  }

  const handleDelete = (order) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      deleteOrderMutation.mutate(order.id)
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingOrder(null)
    reset()
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending':
        return <Clock className="h-4 w-4 text-yellow-600" />
      case 'In Progress':
        return <ShoppingCart className="h-4 w-4 text-blue-600" />
      case 'Completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'Cancelled':
        return <XCircle className="h-4 w-4 text-red-600" />
      default:
        return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return 'badge-warning'
      case 'In Progress':
        return 'badge-secondary'
      case 'Completed':
        return 'badge-success'
      case 'Cancelled':
        return 'badge-danger'
      default:
        return 'badge-secondary'
    }
  }

  const orders = ordersData?.data?.orders || []
  const customers = customersData?.data?.customers || []
  const employees = employeesData?.data?.employees || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-secondary-900">Orders</h1>
          <p className="mt-1 text-base text-secondary-600">
            Manage customer orders and their status
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Order
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>
            <div>
              <label className="label">Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedStatus('')
                }}
                className="btn-outline w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="card">
        <div className="card-content p-0">
          {isLoading ? (
            <LoadingSpinner size="lg" className="h-64" />
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr className="table-row">
                    <th className="table-head">Order</th>
                    <th className="table-head">Customer</th>
                    <th className="table-head">Status</th>
                    <th className="table-head">Amount</th>
                    <th className="table-head">Assigned To</th>
                    <th className="table-head">Date</th>
                    <th className="table-head">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {orders.map((order) => (
                    <tr key={order.id} className="table-row">
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                            <ShoppingCart className="h-4 w-4 text-primary-600" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-secondary-900">
                              {order.orderNumber}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {order.notes}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-6 w-6 bg-secondary-100 rounded-full flex items-center justify-center">
                            <User className="h-3 w-3 text-secondary-600" />
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-secondary-900">
                              {order.customer?.firstName} {order.customer?.lastName}
                            </div>
                            <div className="text-sm text-secondary-500">
                              {order.customer?.phone}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          {getStatusIcon(order.status)}
                          <span className={`ml-1 badge ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          <div>Total: ₹{order.totalAmount}</div>
                          <div>Advance: ₹{order.advancePaid}</div>
                          <div>Balance: ₹{order.balanceAmount}</div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          {order.assignedEmployee?.firstName} {order.assignedEmployee?.lastName}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-secondary-900">
                          <div>Order: {new Date(order.orderDate).toLocaleDateString()}</div>
                          {order.deliveryDate && (
                            <div>Delivery: {new Date(order.deliveryDate).toLocaleDateString()}</div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(order)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(order)}
                            className="text-danger-600 hover:text-danger-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Order Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-start justify-center p-4 py-8">
            <div className="modal-overlay" onClick={handleCloseModal} />
            <div className="modal-content max-w-5xl w-full max-h-[90vh] flex flex-col">
              <div className="flex justify-between items-center mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-secondary-900">
                  {editingOrder ? 'Edit Order' : 'Add New Order'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto">
                <OrderFormSteps
                  onSubmit={onSubmit}
                  onCancel={handleCloseModal}
                  initialData={editingOrder}
                  isLoading={createOrderMutation.isLoading || updateOrderMutation.isLoading}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Orders
